import { createPortal } from "react-dom";
import {
  PoiContributionCard,
  type ContributionAction,
} from "@/components/Map/PoiContributionCard";

type Props = {
  /** True while a venue is being created before navigation. */
  creating?: boolean;
  onSelectAction: (action: ContributionAction) => void;
  onClose: () => void;
  onBack?: () => void;
  /** Only the map's flow has a step to go back to. */
  onGetNotified?: () => void;
  onShowOptions?: () => void;
};

const noop = () => {};

/**
 * The "What you can do" picker: centred modal on desktop, full-screen on mobile.
 * Shared by the map's POI flow and the venue card's "Add info".
 */
export function ContributionOptionsModal({
  creating = false,
  onSelectAction,
  onClose,
  onBack,
  onGetNotified = noop,
  onShowOptions = noop,
}: Props) {
  const card = (variant: "card" | "page") => (
    <PoiContributionCard
      step="options"
      creating={creating}
      variant={variant}
      onGetNotified={onGetNotified}
      onShowOptions={onShowOptions}
      onSelectAction={onSelectAction}
      onClose={onClose}
      onBack={onBack}
    />
  );

  // The venue card hosts this inside its own clickable root, so nothing may
  // bubble out of the modal.
  const contain = {
    onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
  };

  // Portaled to document.body: several callers (e.g. the map marker's
  // VenueCard popup) sit inside an ancestor with a CSS `transform`, which
  // creates a new containing block for `position: fixed` descendants. Left
  // un-portaled, "fixed inset-0" ends up pinned to that ancestor instead of
  // the viewport, cropping the overlay instead of centering it on screen.
  return createPortal(
    <>
      <div
        className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/40 p-4"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        {...contain}
      >
        <div className="drop-shadow-xl" onClick={(e) => e.stopPropagation()}>
          {card("card")}
        </div>
      </div>

      <div
        className="lg:hidden fixed inset-0 z-[1100] bg-bg-brand-secondary-default"
        onClick={(e) => e.stopPropagation()}
        {...contain}
      >
        {card("page")}
      </div>
    </>,
    document.body,
  );
}

export default ContributionOptionsModal;
